import { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Avatar,
  LinearProgress,
  CircularProgress,
  Fade
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as DetailIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  LocalShipping as DeliveryIcon,
  Print as PrintIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { fetchData, deleteData, ConvertEstimateToDelivery } from '../../../apiService';
import EstimatePDFViewer from './EstimatePDFViewer';
import { getCompanyConfigFromAPI } from '../Sale/CompanyConfig';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import EstimateModal from './EstimateModal';
import DeliveryOutModal from '../Delivery/DeliveryOutModal';
import Pagination from '../../common/Pagination';

function EstimateIndex() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [fetchingEstimate, setFetchingEstimate] = useState(false);

  // Delivery Out Modal states
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryData, setDeliveryData] = useState(null);
  const [loadingDeliveryData, setLoadingDeliveryData] = useState(false);

  // Data for modal
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  // Company / branch details for print header
  const [companyDetails, setCompanyDetails] = useState(null);
  const [branchDetails, setBranchDetails] = useState(null);

  // PDF Viewer states
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedEstimateForPDF, setSelectedEstimateForPDF] = useState(null);
  const [selectedEstimateDetailsForPDF, setSelectedEstimateDetailsForPDF] = useState([]);
  const [companyData, setCompanyData] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Statistics states
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    approved: 0,
    converted: 0
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    applyFilters();
  }, [data, searchTerm]);

  const fetchTableData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData('EstimateApi', `/EstimateList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      if (response && response.responseCode === '1111' && Array.isArray(response.response_data)) {
        const estimates = response.response_data;
        setData(estimates);
        setStatistics({
          total: estimates.length,
          pending: estimates.filter(item => item.Status === 'Pending').length,
          inProgress: estimates.filter(item => item.Status === 'In Progress').length,
          approved: estimates.filter(item => item.Status === 'Approved').length,
          converted: estimates.filter(item => item.Status === 'Converted').length
        });
      } else {
        setData([]);
        setStatistics({ total: 0, pending: 0, inProgress: 0, approved: 0, converted: 0 });
      }
    } catch (error) {
      console.error('Error fetching Estimate Details:', error);
      setData([]);
      setStatistics({ total: 0, pending: 0, inProgress: 0, approved: 0, converted: 0 });
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, fromDate, toDate, token]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetchData('CustomerApi', `/${companyId}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        setCustomers(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [companyId, token]);

  const fetchItems = useCallback(async () => {
    try {
      const response = await fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        setItems(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  }, [companyId, branchId, token]);

  const fetchEnquiries = useCallback(async () => {
    try {
      const response = await fetchData('EnquiryApi', `/EnquiryList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        setEnquiries(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    }
  }, [companyId, branchId, fromDate, toDate, token]);

  const fetchCompanyDetails = useCallback(async () => {
    try {
      const data = await getCompanyConfigFromAPI(companyId, branchId, token);
      setCompanyData(data);
      setCompanyDetails(data);
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  }, [companyId, branchId, token]);

  // Keep the full-screen loading state focused on the list request. Supporting
  // lookups continue in the background and no longer hold the estimates hostage.
  useEffect(() => {
    if (!companyId || !branchId || !token) return;
    fetchTableData();
  }, [companyId, branchId, token, fetchTableData]);

  useEffect(() => {
    if (!companyId || !branchId || !token) return;
    fetchCustomers();
    fetchItems();
    fetchCompanyDetails();
  }, [companyId, branchId, token, fetchCustomers, fetchItems, fetchCompanyDetails]);

  useEffect(() => {
    if (!companyId || !branchId || !token) return;
    fetchEnquiries();
  }, [companyId, branchId, token, fetchEnquiries]);

  // ─── Print Estimate (PDF Viewer) ─────────────────────────────────────────
  const handlePrintEstimate = async (estimateId) => {
    try {
      const response = await fetchData('EstimateApi', `/${estimateId}`, token);
      let rawData = response.response_data || response;
      const estimate = rawData.Estimate ? rawData.Estimate : rawData;
      const details  = rawData.EstimateDetails || rawData.items || [];
      setSelectedEstimateForPDF(estimate);
      setSelectedEstimateDetailsForPDF(details);
      setPdfViewerOpen(true);
    } catch (err) {
      console.error('Error loading estimate for PDF:', err);
      alert('Failed to load estimate. Please try again.');
    }
  };

  // ─── WhatsApp Estimate ────────────────────────────────────────────────────
  const handleWhatsAppEstimate = async (estimateId) => {
    try {
      const response = await fetchData('EstimateApi', `/${estimateId}`, token);
      let rawData = response.response_data || response;
      const estimate = rawData.Estimate ? rawData.Estimate : rawData;
      const details  = rawData.EstimateDetails || rawData.items || [];

      const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB') : 'N/A';
      const fmtC = (v) => v ? `Rs.${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'Rs.0.00';

      const companyName = companyDetails?.CompanyName || companyDetails?.Name || '';
      const branchName  = branchDetails?.BranchName  || branchDetails?.Name  || '';

      let msg = '';
      if (companyName) msg += `*${companyName}*`;
      if (branchName)  msg += ` - ${branchName}`;
      if (msg)         msg += '\n';

      msg += `*ESTIMATE*\n`;
      msg += `-----------------------------\n`;
      msg += `*Estimate No :* ${estimate.EstimateNo || `EST-${estimate.BillNumber}`}\n`;
      msg += `*Date        :* ${fmtD(estimate.BillDate)}\n`;
      msg += `*Customer    :* ${estimate.CustomerName || 'N/A'}\n`;
      if (estimate.ContactNumber1) msg += `*Contact     :* ${estimate.ContactNumber1}\n`;
      if (estimate.EnquiryNo)      msg += `*Enquiry No  :* ${estimate.EnquiryNo}\n`;
      if (estimate.EventName)      msg += `*Event       :* ${estimate.EventName}\n`;
      if (estimate.EventLocation)  msg += `*Venue       :* ${estimate.EventLocation}\n`;
      if (estimate.ProgramDate)    msg += `*Program Date:* ${fmtD(estimate.ProgramDate)}\n`;
      if (estimate.EventDays)      msg += `*Event Days  :* ${estimate.EventDays}\n`;
      if (estimate.DeliveryDate)   msg += `*Delivery    :* ${fmtD(estimate.DeliveryDate)}\n`;
      if (estimate.DeliveryReturnDate) msg += `*Return      :* ${fmtD(estimate.DeliveryReturnDate)}\n`;

      if (details.length > 0) {
        msg += `\n*ITEMS*\n`;
        msg += `-----------------------------\n`;
        details.forEach((item, i) => {
          msg += `\n*${i + 1}. ${item.ItemName}*\n`;
          msg += `   Qty: ${item.Quantity}  |  Days: ${item.RentDays || item.EventDays || '-'}  |  Rate: ${fmtC(item.Rate)}\n`;
          if (parseFloat(item.DiscountAmount || 0) > 0)
            msg += `   Discount: ${fmtC(item.DiscountAmount)}\n`;
          msg += `   Amount: *${fmtC(item.TotalAmount)}*\n`;
        });
      }

      msg += `\n-----------------------------\n`;
      msg += `Sub Total    : ${fmtC(estimate.Amount)}\n`;
      if (parseFloat(estimate.DiscountAmount || 0) > 0)
        msg += `Discount     : - ${fmtC(estimate.DiscountAmount)}\n`;
      if (parseFloat(estimate.CGSTAmount || 0) > 0)
        msg += `CGST         : ${fmtC(estimate.CGSTAmount)}\n`;
      if (parseFloat(estimate.SGSTAmount || 0) > 0)
        msg += `SGST         : ${fmtC(estimate.SGSTAmount)}\n`;
      if (parseFloat(estimate.TransportingCharge || 0) > 0)
        msg += `Transport    : ${fmtC(estimate.TransportingCharge)}\n`;
      msg += `*TOTAL AMOUNT : ${fmtC(estimate.TotalAmount)}*\n`;
      if (parseFloat(estimate.AdvanceAmount || 0) > 0) {
        msg += `Advance Paid : ${fmtC(estimate.AdvanceAmount)}\n`;
        msg += `*Balance Due  : ${fmtC(estimate.BalanceAmount)}*\n`;
      }

      if (estimate.Remarks) {
        msg += `\n*Remarks:* ${estimate.Remarks}\n`;
      }

      const phone = (estimate.ContactNumber1 || '').replace(/[^0-9]/g, '');
      const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(whatsappUrl, '_blank');
    } catch (err) {
      console.error('Error sending WhatsApp message:', err);
      alert('Failed to open WhatsApp. Please try again.');
    }
  };

  const applyFilters = () => {
    if (!Array.isArray(data)) {
      setFilteredData([]);
      return;
    }

    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.EstimateNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ContactNumber1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.EnquiryNo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteData('EstimateApi', id, token);
      if (response && response.responseCode === '1111') {
        await fetchTableData();
      }
    } catch (error) {
      console.error('Error deleting Estimate:', error);
    }
    setConfirmDialogOpen(false);
    setDeleteItemId(null);
  };

  const fetchSingleEstimate = async (estimateId) => {
    setFetchingEstimate(true);
    try {
      const response = await fetchData('EstimateApi', `/${estimateId}`, token);
      let estimateData = response.response_data || response;

      if (estimateData.Estimate && estimateData.EstimateDetails) {
        return {
          ...estimateData.Estimate,
          EstimateDetails: estimateData.EstimateDetails
        };
      }

      return estimateData;
    } catch (error) {
      console.error('Error fetching single Estimate:', error);
      return null;
    } finally {
      setFetchingEstimate(false);
    }
  };

  const handleModalSave = async () => {
    await fetchTableData();
    setModalOpen(false);
    setSelectedItem(null);
    setModalMode('create');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const handleRefresh = () => {
    fetchTableData();
  };

  const handleConvertToDelivery = async (estimateId) => {
    setLoadingDeliveryData(true);
    try {
      // Fetch the full estimate data first
      const estimateResponse = await fetchData('EstimateApi', `/${estimateId}`, token);
      console.log('Estimate data response:', estimateResponse);

      let estimateData = estimateResponse.response_data || estimateResponse;

      // Handle different response structures
      if (estimateData.Estimate && estimateData.EstimateDetails) {
        console.log('Response has separate Estimate and EstimateDetails');
        estimateData = {
          ...estimateData.Estimate,
          EstimateDetails: estimateData.EstimateDetails
        };
      }

      console.log('Opening DeliveryOutModal with estimate data:', estimateData);
      setDeliveryData(estimateData);
      setDeliveryModalOpen(true);
    } catch (error) {
      console.error('Error fetching estimate for delivery:', error);
      alert('Error loading estimate data. Please try again.');
    } finally {
      setLoadingDeliveryData(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 9999,
              backdropFilter: 'blur(4px)'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: "#0d766e",
                  mb: 2
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "#0d766e",
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                Loading Estimates...
              </Typography>
            </Box>
          </Box>
        )}
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        <div className="suite-page-panel" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: "Segoe UI, sans-serif",
        }}>
          {/* Header Section */}
          <div className="suite-section-heading" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: "Segoe UI, sans-serif",
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: "Segoe UI, sans-serif", }}>Estimate Management</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
          <div style={{
            marginBottom: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: "#0d766e" },
                      '&.Mui-focused fieldset': { borderColor: "#0d766e" },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: "#0d766e" },
                      '&.Mui-focused fieldset': { borderColor: "#0d766e" },
                    },
                  }}
                />
              </div>

              {/* Search - Icon on mobile, always visible on desktop */}
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': {
                          borderColor: "#0d766e",
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: "#0d766e",
                        },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: "#0d766e",
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: "#0d766e",
                      },
                    },
                  }}
                />
              )}

              <Tooltip title="Create a new estimate" arrow>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSelectedItem(null);
                    setModalMode('create');
                    setModalOpen(true);
                  }}
                  className="action-button"
                  style={{
                    background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                    color: 'white',
                    padding: isMobile ? '0' : '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: "0 2px 8px rgba(13, 118, 110, 0.4)",
                    border: 'none',
                    height: '32px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '32px' : '100px',
                    width: 'auto',
                    marginLeft: 'auto'
                  }}
                >
                  <AddIcon style={{ fontSize: '18px' }} />
                  {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                </Button>
              </Tooltip>
            </div>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="estimate table">
                <TableHead>
                  <TableRow style={{
                    background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                    height: '28px'
                  }}>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Estimate
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Customer
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Enquiry
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Dates
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Amount
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                          No estimates found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row) => (
                      <TableRow
                        key={row.Id}
                        sx={{
                          '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                          '&:hover': {
                            backgroundColor: '#f1f5f9',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease-in-out'
                          },
                          borderBottom: '1px solid #f1f5f9'
                        }}
                      >
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                            {row.EstimateNo || `EST-${String(row.BillNumber).padStart(4, '0')}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                            {formatDate(row.BillDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.3, fontSize: '0.75rem' }}>
                            {row.CustomerName || 'N/A'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                            {row.ContactNumber1 || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {row.EnquiryNo || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: '#6b7280' }}>
                            Program: {formatDate(row.ProgramDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#059669' }}>
                            {formatCurrency(row.TotalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1, textAlign: 'right' }}>
                          <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  const fullData = await fetchSingleEstimate(row.Id);
                                  setSelectedItem(fullData);
                                  setModalMode('view');
                                  setModalOpen(true);
                                }}
                                sx={{
                                  color: '#3b82f6',
                                  backgroundColor: '#eff6ff',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: '#dbeafe',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <DetailIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit Estimate">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  const fullData = await fetchSingleEstimate(row.Id);
                                  setSelectedItem(fullData);
                                  setModalMode('edit');
                                  setModalOpen(true);
                                }}
                                sx={{
                                  color: '#f59e0b',
                                  backgroundColor: '#fffbeb',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: '#fef3c7',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Convert to Delivery Out">
                              <IconButton
                                size="small"
                                onClick={() => handleConvertToDelivery(row.Id)}
                                sx={{
                                  color: '#8b5cf6',
                                  backgroundColor: '#f5f3ff',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: '#ede9fe',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <DeliveryIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>


                            <Tooltip title="Send via WhatsApp (with PDF)" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handlePrintEstimate(row.Id)}
                                sx={{
                                  color: '#ffffff',
                                  backgroundColor: '#25D366',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: '#1ebe5d',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <WhatsAppIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete Estimate">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDeleteItemId(row.Id);
                                  setConfirmDialogOpen(true);
                                }}
                                sx={{
                                  color: '#ef4444',
                                  backgroundColor: '#fef2f2',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: '#fecaca',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon />
              Confirm Deletion
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <DialogContentText>
              Are you sure you want to delete this estimate? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined">Cancel</Button>
            <Button onClick={() => handleDelete(deleteItemId)} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Loading Dialog */}
        <Dialog open={fetchingEstimate}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
            <CircularProgress size={48} />
            <Typography>Loading estimate details...</Typography>
          </DialogContent>
        </Dialog>

        {/* Estimate Modal */}
        <EstimateModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedItem(null);
            setModalMode('create');
          }}
          onSave={handleModalSave}
          estimateData={selectedItem}
          mode={modalMode}
          customers={customers}
          items={items}
          enquiries={enquiries}
        />

        {/* Delivery Out Modal */}
        <DeliveryOutModal
          open={deliveryModalOpen}
          onClose={() => {
            setDeliveryModalOpen(false);
            setDeliveryData(null);
          }}
          onSave={() => {
            setDeliveryModalOpen(false);
            setDeliveryData(null);
            fetchTableData();
          }}
          deliveryData={deliveryData}
          mode="create"
        />

        {/* Estimate PDF Viewer */}
        <EstimatePDFViewer
          open={pdfViewerOpen}
          onClose={() => {
            setPdfViewerOpen(false);
            setSelectedEstimateForPDF(null);
            setSelectedEstimateDetailsForPDF([]);
          }}
          estimateData={selectedEstimateForPDF}
          estimateDetails={selectedEstimateDetailsForPDF}
          companyData={companyData}
        />
      </div>
    </Fade>
  );
}

export default EstimateIndex;
